<template>
  <div class="bg-white shadow rounded-lg dark:bg-neutral-800 dark:shadow-none relative">
    <!-- Notifications -->
    <NotificationPopup ref="notification" :type="notificationType" :title="notificationTitle" :text="notificationText" />
    <!-- Accredited State -->
    <div v-if="verification_status == 'Verified'" class="relative">
      <div class="absolute inset-0 custom-bg rounded-full"></div>
      <div class="relative p-5">
        <AppIconHeader>
          <div class="relative">
            <UserIcon class="size-8 text-operacolor" />
            <div class="absolute -bottom-1 -right-1 flex items-center justify-center size-5">
              <div class="relative size-5">
                <SolidCheckBadgeIcon class="size-5 text-white dark:text-green-500 absolute z-10 top-0.5 left-0" />
                <div class="bg-green-500 dark:bg-black size-3 absolute rounded-full top-1.5 left-1"></div>
              </div>
            </div>
          </div>
        </AppIconHeader>

        <div class="flex flex-col items-center justify-center mt-1.5">
          <div class="text-md font-semibold text-gray-900 dark:text-neutral-200 mb-3">Investor Verification</div>
          <div class="flex items-center gap-1 mb-2 badge_green">
            Verified
            <CheckBadgeIcon class="w-4 h-4" />
          </div>
          <div class="text-sm text-gray-500 dark:text-neutral-400 font-light">Your identity has been verified</div>
          <div class="text-sm text-gray-500 dark:text-neutral-400 font-light">no further action is required.</div>
          <div class="flex gap-5 mt-4">
            <AppTooltip
              content="This will toggle a view of the documents you previously submitted to verify your identity."
              placement="top"
              max-width="250px"
            >
              <AppButtonSecondary
                :text="showDocsText"
                :icon="ClockIcon"
                :icon-first="true"
                size="lg"
                color="less-dark"
                @click="showDocs = !showDocs"
              />
            </AppTooltip>
            <AppButtonPrimary v-if="userStore.user.profiles_protected.role == 'super_admin' || userStore.user.profiles_protected.role == 'partner_admin'" :text="showEditorText" :icon="PencilSquareIcon" size="lg" @click="showEditor = !showEditor" />
          </div>
          <div v-if="showDocs" class="flex gap-5 w-full mt-4 bg-white dark:bg-neutral-800 max-w-3xl">
            <DragDrop
              class="w-full"
              text="or drag and drop"
              type="verification"
              :files="documents"
              :downloadable="true"
              accepted_text="PDF, JPG, or PNG (Max size 50 MB)"
              @changed-files="filesChanged"
            />
          </div>
        </div>
        <!-- Update KYC AML -->
        <div
          v-if="showEditor && (userStore.user.profiles_protected.role == 'super_admin' || userStore.user.profiles_protected.role == 'partner_admin')"
          class="flex flex-wrap gap-5 mt-5 mx-1"
        >
          <div class="flex-1 flex gap-5 flex-wrap justify-between items-end bg-gray-100 shadow-inner rounded-lg p-3">
            <div class="flex gap-5 flex-wrap items-end">
              <div class="w-40">
                <AppSelect
                  v-model="kyc_status"
                  :items="kycAmlOptions"
                  label="KYC Status"
                  info="This is the 'Know Your Customer' status. Often we can approve investors automatically based on their basic information. In some cases investors will fail and need to upload additional documents to pass KYC."
                />
              </div>
              <div class="w-40">
                <AppSelect
                  v-model="aml_status"
                  :items="kycAmlOptions"
                  label="AML Status"
                  info="This is the 'Anti Money Laundering' status. Often we can approve investors automatically based on their basic information. In some cases investors will fail and need to upload additional documents to pass AML."
                />
              </div>
            </div>
            <div class="mr-2">
              <AppButtonPrimary
                text="Save Changes"
                :icon="CloudArrowUpIcon"
                :loading="updating_kyc_aml_status"
                size="lg"
                color="yellow"
                @click="updatingKycAmlStatuses()"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Under Review State -->
    <div v-else-if="verification_status == 'Under Review'" class="p-5">
      <div
        v-if="userStore.user.profiles_protected.role == 'partner_admin' || userStore.user.profiles_protected.role == 'super_admin'"
        class="absolute top-1 left-2 cursor-default"
      >
        <AppTooltip :content="`${needHelpText}`" placement="top" max-width="400px">
          <div class="rounded-full flex items-center gap-0.5 bg-gray-100 px-2 py-0.5">
            <div class="text-[10px]">Need Help</div>
            <QuestionMarkCircleIcon class="size-3 text-black" />
          </div>
        </AppTooltip>
      </div>
      <AppIconHeader>
        <div class="relative">
          <UserIcon class="size-8 text-operacolor" />
          <div class="absolute -bottom-1 -right-1 flex items-center justify-center size-5">
            <div class="relative size-5">
              <SolidClockIcon class="size-5 text-yellow-400 absolute z-10 top-0.5 left-0" />
              <div class="bg-white dark:bg-black size-3 absolute rounded-full top-1.5 left-1"></div>
            </div>
          </div>
        </div>
      </AppIconHeader>
      <div class="flex flex-col items-center justify-center mt-1.5">
        <div class="text-md font-semibold text-gray-900 dark:text-neutral-200 mb-3">Investor Verification</div>
        <div class="flex items-center gap-1 mb-2 badge_blue">
          Under Review
          <ClockIcon class="w-4 h-4" />
        </div>
        <div class="text-sm text-gray-500 dark:text-neutral-400 font-light max-w-56 text-center">
          Your documents are under review. <b class="text-gray-800">No action is required</b> at this time.
        </div>
        <div class="flex gap-5 w-full mt-4 bg-white dark:bg-neutral-800 max-w-3xl">
          <DragDrop
            class="w-full"
            text="or drag and drop"
            type="verification"
            :files="documents"
            :downloadable="true"
            accepted_text="PDF, JPG, or PNG (Max size 50 MB)"
            @changed-files="filesChanged"
          />
        </div>
      </div>
      <div
        v-if="userStore.user.profiles_protected.role == 'super_admin' || userStore.user.profiles_protected.role == 'partner_admin'"
        class="flex flex-wrap gap-5 mt-5 mx-1"
      >
        <div class="flex-1 flex gap-5 flex-wrap justify-between items-end bg-gray-100 shadow-inner rounded-lg p-3">
          <div class="flex gap-5 flex-wrap items-end">
            <div class="w-40">
              <AppSelect
                v-model="kyc_status"
                :items="kycAmlOptions"
                label="KYC Status"
                info="This is the 'Know Your Customer' status. Often we can approve investors automatically based on their basic information. In some cases investors will fail and need to upload additional documents to pass KYC."
              />
            </div>
            <div class="w-40">
              <AppSelect
                v-model="aml_status"
                :items="kycAmlOptions"
                label="AML Status"
                info="This is the 'Anti Money Laundering' status. Often we can approve investors automatically based on their basic information. In some cases investors will fail and need to upload additional documents to pass AML."
              />
            </div>
          </div>
          <div class="mr-2">
            <AppButtonPrimary
              text="Save Changes"
              :icon="CloudArrowUpIcon"
              :loading="updating_kyc_aml_status"
              size="lg"
              color="yellow"
              @click="updatingKycAmlStatuses()"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Action Required State -->
    <div v-else-if="verification_status == 'Action Required'" class="p-5">
      <div
        v-if="userStore.user.profiles_protected.role == 'partner_admin' || userStore.user.profiles_protected.role == 'super_admin'"
        class="absolute top-1 left-2 cursor-default"
      >
        <AppTooltip :content="`${needHelpText}`" placement="top" max-width="400px">
          <div class="rounded-full flex items-center gap-0.5 bg-gray-100 px-2 py-0.5">
            <div class="text-[10px]">Need Help</div>
            <QuestionMarkCircleIcon class="size-3 text-black" />
          </div>
        </AppTooltip>
      </div>
      <AppIconHeader>
        <div class="relative">
          <UserIcon class="size-8 text-operacolor" />
          <div class="absolute -bottom-1 -right-1 flex items-center justify-center size-5">
            <div class="relative size-5">
              <SolidExclamationTriangleIcon class="size-5 text-yellow-400 absolute z-10 top-0.5 left-0" />
              <div class="bg-white dark:bg-black size-2 absolute rounded-full top-2 left-1.5"></div>
            </div>
          </div>
        </div>
      </AppIconHeader>
      <div class="flex flex-col items-center justify-center mt-1.5">
        <div class="text-md font-semibold text-gray-900 dark:text-neutral-200 mb-3">Investor Verification</div>
        <AppTooltip :content="`${verification_notes}`" placement="top" max-width="350px">
          <div class="flex items-center gap-1 mb-2 badge_orange">
            Action Required
            <ExclamationTriangleIcon class="w-4 h-4" />
          </div>
        </AppTooltip>
        <div class="text-sm text-gray-500 dark:text-neutral-400 font-light max-w-72 text-center">
          The information submitted was insufficient to qualified you as an accredited investor. Please submit additional information.
        </div>
        <div v-if="props.party?.parties_protected?.notes" class="text-sm text-gray-500 dark:text-neutral-400 max-w-72 text-center mt-2">
          <b>Reason for pending status:</b>
          {{ props.party?.parties_protected?.notes }}
        </div>
        <div class="flex gap-5 w-full mt-4 bg-white dark:bg-neutral-800 max-w-3xl">
          <DragDrop
            class="w-full"
            text="or drag and drop"
            type="verification"
            :files="documents"
            :downloadable="true"
            accepted_text="PDF, JPG, or PNG (Max size 50 MB)"
            @changed-files="filesChanged"
          />
        </div>
      </div>
      <div
        v-if="userStore.user.profiles_protected.role == 'super_admin' || userStore.user.profiles_protected.role == 'partner_admin'"
        class="flex flex-wrap gap-5 mt-5 mx-1"
      >
        <div class="flex-1 flex gap-5 flex-wrap justify-between items-end bg-gray-100 shadow-inner rounded-lg p-3">
          <div class="flex gap-5 flex-wrap items-end">
            <div class="w-40">
              <AppSelect
                v-model="kyc_status"
                :items="kycAmlOptions"
                label="KYC Status"
                info="This is the 'Know Your Customer' status. Often we can approve investors automatically based on their basic information. In some cases investors will fail and need to upload additional documents to pass KYC."
              />
            </div>
            <div class="w-40">
              <AppSelect
                v-model="aml_status"
                :items="kycAmlOptions"
                label="AML Status"
                info="This is the 'Anti Money Laundering' status. Often we can approve investors automatically based on their basic information. In some cases investors will fail and need to upload additional documents to pass AML."
              />
            </div>
          </div>
          <div class="mr-2">
            <AppButtonPrimary
              text="Save Changes"
              :icon="CloudArrowUpIcon"
              :loading="updating_kyc_aml_status"
              size="lg"
              color="yellow"
              @click="updatingKycAmlStatuses()"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Not Accredited State -->
    <div v-else class="p-5">
      <div
        v-if="userStore.user.profiles_protected.role == 'partner_admin' || userStore.user.profiles_protected.role == 'super_admin'"
        class="absolute top-1 left-2 cursor-default"
      >
        <AppTooltip :content="`${needHelpText}`" placement="top" max-width="400px">
          <div class="rounded-full flex items-center gap-0.5 bg-gray-100 px-2 py-0.5">
            <div class="text-[10px]">Need Help</div>
            <QuestionMarkCircleIcon class="size-3 text-black" />
          </div>
        </AppTooltip>
      </div>
      <AppIconHeader>
        <div class="relative">
          <UserIcon class="size-8 text-operacolor" />
          <div class="absolute -bottom-1 -right-1 flex items-center justify-center size-5">
            <div class="relative size-5">
              <SolidXCircleIcon class="size-5 text-red-400 absolute z-10 top-0.5 left-0" />
              <div class="bg-white dark:bg-black size-3 absolute rounded-full top-1.5 left-1"></div>
            </div>
          </div>
        </div>
      </AppIconHeader>
      <div class="flex flex-col items-center justify-center mt-1.5">
        <div class="text-md font-semibold text-gray-900 dark:text-neutral-200 mb-3">Investor Verification</div>
        <div class="flex items-center gap-1 mb-2 badge_red">
          Verification Failed
          <XCircleIcon class="w-4 h-4" />
        </div>
        <div class="text-sm text-gray-500 dark:text-neutral-400 font-light max-w-72 text-center">
          The information submitted was insufficient to qualified you as an accredited investor. Please submit additional information.
        </div>
        <div v-if="props.party?.parties_protected?.notes" class="text-sm text-gray-500 dark:text-neutral-400 max-w-72 text-center mt-2">
          <b>Reason for rejection:</b>
          {{ props.party?.parties_protected?.notes }}
        </div>
        <div class="flex gap-5 w-full mt-4 bg-white dark:bg-neutral-800 max-w-3xl">
          <DragDrop
            class="w-full"
            text="or drag and drop"
            type="verification"
            :files="documents"
            :downloadable="true"
            accepted_text="PDF, JPG, or PNG (Max size 50 MB)"
            @changed-files="filesChanged"
          />
        </div>
      </div>
      <div
        v-if="userStore.user.profiles_protected.role == 'super_admin' || userStore.user.profiles_protected.role == 'partner_admin'"
        class="flex flex-wrap gap-5 mt-5 mx-1"
      >
        <div class="flex-1 flex gap-5 flex-wrap justify-between items-end bg-gray-100 shadow-inner rounded-lg p-3">
          <div class="flex gap-5 flex-wrap items-end">
            <div class="w-40">
              <AppSelect
                v-model="kyc_status"
                :items="kycAmlOptions"
                label="KYC Status"
                info="This is the 'Know Your Customer' status. Often we can approve investors automatically based on their basic information. In some cases investors will fail and need to upload additional documents to pass KYC."
              />
            </div>
            <div class="w-40">
              <AppSelect
                v-model="aml_status"
                :items="kycAmlOptions"
                label="AML Status"
                info="This is the 'Anti Money Laundering' status. Often we can approve investors automatically based on their basic information. In some cases investors will fail and need to upload additional documents to pass AML."
              />
            </div>
          </div>
          <div class="mr-2">
            <AppButtonPrimary
              text="Save Changes"
              :icon="CloudArrowUpIcon"
              :loading="updating_kyc_aml_status"
              size="lg"
              color="yellow"
              @click="updatingKycAmlStatuses()"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Essentials
import { ref, onMounted, computed } from "vue";

// Components
import NotificationPopup from "@/components/applicationui/AppNotification.vue";
import DragDrop from "@/components/applicationinputs/documents/DragDrop.vue";
import AppIconHeader from "@/components/applicationui/AppIconHeader.vue";
import AppTooltip from "@/components/applicationui/AppTooltip.vue";
import AppButtonSecondary from "@/components/applicationui/AppButtonSecondary.vue";
import AppButtonPrimary from "@/components/applicationui/AppButtonPrimary.vue";
import AppSelect from "@/components/applicationinputs/AppSelect.vue";

// Libraries
import {
  CheckBadgeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  XCircleIcon,
  CloudArrowUpIcon,
  PencilSquareIcon,
} from "@heroicons/vue/24/outline";
import {
  UserIcon,
  XCircleIcon as SolidXCircleIcon,
  ClockIcon as SolidClockIcon,
  CheckBadgeIcon as SolidCheckBadgeIcon,
  ExclamationTriangleIcon as SolidExclamationTriangleIcon,
} from "@heroicons/vue/24/solid";

// Props
const props = defineProps({
  party: { type: Object, required: true, default: () => null }, //Expects account object
  user_id: { type: String, required: true, default: null }, //Expects the current user id
});

// Emits
const emit = defineEmits(["success"]);

// Stores
import { useUserStore } from "@/stores/user";
const userStore = useUserStore();
import { usePartiesStore } from "@/stores/parties";
const partiesStore = usePartiesStore();
import { usePartnerStore } from "@/stores/partner";
const partnerStore = usePartnerStore();

// Computed Statuses START
const kyc_status = ref(props.party?.parties_protected?.kyc);
const aml_status = ref(props.party?.parties_protected?.aml);

const verification_status = computed(() => {
  if (kyc_status.value == "approved" && aml_status.value == "approved") return "Verified";
  else if (kyc_status.value == "disapproved" && aml_status.value == "disapproved") return "Verification Failed";
  else if (kyc_status.value == "pending" || aml_status.value == "pending") return "Under Review";
  else return "Action Required";
});

const verification_notes = computed(() => {
  if (kyc_status.value == "disapproved" && aml_status.value == "disapproved")
    return "KYC and AML verification have failed. Please upload additional documents to verify your identity.";
  else if (kyc_status.value == "disapproved" && aml_status.value == "approved")
    return "KYC verification has failed. Please upload additional documents to verify your identity.";
  else if (kyc_status.value == "approved" && aml_status.value == "disapproved")
    return "AML verification has failed. Please upload additional documents to verify your identity.";
  else if (kyc_status.value != "pending" && aml_status.value == "pending")
    return "AML verification is under Review. You can still upload additional files";
  else if (kyc_status.value == "pending" && aml_status.value != "pending")
    return "KYC verification is under Review. You can still upload additional files";
  else if (kyc_status.value == "pending" && aml_status.value == "pending") return "KYC and AML verification are under review.";
  else {
    return "Investor verification is under review.";
  }
});

const needHelpText = computed(() => {
  if (partnerStore.partner.partners_protected.managed_kyc_aml) {
    if (verification_status.value == "Verified") {
      return "This investors verification status is Verified. No action is required.";
    } else if (verification_status.value == "Under Review") {
      return "This investors verification status is Under Review, by us! Don't worry we will take it from here. If for some reason this investor needs to be approved quickly or if it has been more than 3 business days, please reach out to us directly.";
    } else if (verification_status.value == "Action Required") {
      return "This investors verification status is Action Required. Which means the investors KYC or AML was disapproved and they have not uploaded any additional documents to verify their identity. Don't worry the investor has been notified. If for some reason this investor needs to be approved quickly or if it has been more than 3 business days, please reach out to us directly.";
    } else if (verification_status.value == "Verification Failed") {
      return "This investors verification status has failed, which means either they did not upload any additional verification documents after failing the automated KYC & AML check, or an admin like yourself has manually disapproved them. If for some reason this investor needs to be approved quickly or if it has been more than 3 business days, please reach out to us directly.";
    }
  }
  if (verification_status.value == "Verified") {
    return "This investors verification status is Verified. No action is required.";
  } else if (verification_status.value == "Under Review") {
    return "This investors verification status is Under Review, by you! This means they failed the automated KYC or AML checks but then followed the prompt to upload additonal documents to verify their identity. You or a member of your team should now review these document and then set the customers status to Approved or Disapproved. If the documents uploaded were not sufficient you can set the KYC or AML status to disapproved which will automatically notify the investor that more information is needed. You can also reach out to this investor directly to get the needed documents to confirm their identity.";
  } else if (verification_status.value == "Action Required") {
    return "This investors verification status is Action Required. Which means the investors KYC or AML was disapproved and they have not uploaded any additional documents to verify their identity. You or a member of your team may want to reach out to this investor directly to get need documents to confirm their identity, keep in mind this investor was notified via email that they need to upload more information when their status changed to Action Required.";
  } else if (verification_status.value == "Verification Failed") {
    return "This investors verification status has failed, which means either they did not upload any additional verification documents after failing the automated KYC & AML check, or an admin like yourself has manually disapproved them. If you would like to get them approved you can rerun KYC and AML using the buttons at the bottom of this slideover or you can reach out to this investor directly to get the documents needed to confirm their identity.";
  }
  return "";
});
// Computed Statuses END

// Update KYC/AML Statuses START
const updating_kyc_aml_status = ref(false);
const showEditor = ref(false);
const showEditorText = computed(() => {
  return showEditor.value ? "Hide Editor" : "Show Editor";
});
const kycAmlOptions = ref(["approved", "disapproved", "pending"]);
async function updatingKycAmlStatuses() {
  updating_kyc_aml_status.value = true;
  const successful = await partiesStore.updateKycAndAmlStatus(
    props.party.id,
    props.party.tapi_party_id,
    kyc_status.value,
    aml_status.value,
    props.party.partner_id,
  );
  if (!successful) {
    notify(
      "failure",
      "Sorry",
      "We were unable to update the KYC/AML status. Please try again later, if the issue persists please contact tech support",
    );
    return;
  }
  updating_kyc_aml_status.value = false;
  emit("success");
  notify("success", "KYC/AML Status Updated", "The KYC/AML status was updated successfully");
}
// Update KYC/AML Statuses END

// Hide and show docs START
const showDocs = ref(false);
const showDocsText = computed(() => {
  return showDocs.value ? "Hide Docs" : "View Docs";
});
// Hide and show docs END

// Get existing verification documents START
const documents = ref([]);
async function setInvestorDocuments() {
  // Set default values
  let docs = [];
  let docs2 = [];
  // Get all accredited documents
  docs = await partiesStore.getPartyDocuments(props.user_id, props.party.id, props.party.tapi_party_id);
  if (props.party.user_id != props.user_id)
    docs2 = await partiesStore.getPartyDocuments(props.party.user_id, props.party.id, props.party.tapi_party_id);
  if (!docs && !docs2) {
    notify("failure", "Sorry", "We were unable to load your documents. Please try again later, if the issue persists please contact tech support");
    return;
  }
  // Set documents
  documents.value = docs.concat(docs2);
}
// Get existing verification documents END

// Handle files changed START
async function filesChanged(dtNew, removedFileName) {
  if (removedFileName) {
    await handleFileRemoval(removedFileName);
  } else if (dtNew && dtNew.files.length > documents.value.length) {
    await handleUpload(dtNew);
  }
}
// Handle files changed END

// Handle file removal START
async function handleFileRemoval(removedFileName) {
  const fileToRemove = documents.value.find((doc) => doc.name === removedFileName);
  if (!fileToRemove) return;

  const timestamp = Date.now();
  const successful = await partiesStore.archiveInvestorFile(
    `${fileToRemove.userFolder}/investor/${fileToRemove.partyFolder}/${fileToRemove.name}`,
    `${fileToRemove.userFolder}/archived/${fileToRemove.partyFolder}/${timestamp}_${fileToRemove.name}`,
  );
  if (!successful) {
    notify("failure", "Sorry", "We were unable to archive your file. Please try again later, if the issue persists please contact tech support");
    return;
  }
  notify("success", "Document Archived", "Your document was archived successfully");
  emit("success");
  setInvestorDocuments();
}
// Handle file removal END

// Handle file upload START
async function handleUpload(dtNew) {
  const newFiles = Array.from(dtNew.files).filter((newFile) => !documents.value.some((existingFile) => existingFile.name === newFile.name));

  // Upload the files to TAPI and Supabase
  const uploadPromises = [];
  for (const file of newFiles) {
    // Add the TAPI upload promise to the array
    uploadPromises.push(
      partiesStore.uploadPartyDocumentToTapi(
        props.party.id,
        props.party.tapi_party_id,
        file,
        partnerStore.partner.partners_protected.managed_kyc_aml,
      ),
    );
    // Add the Supabase upload promise to the array
    uploadPromises.push(partiesStore.uploadInvestorDocumentToSupabase(props.user_id, props.party.id, file));
  }

  // Wait for all promises to resolve
  const results = await Promise.allSettled(uploadPromises);

  // Check if any of the API calls failed
  const anyFailed = results.some((result) => result.status != "fulfilled" || !result.value);
  // If any of the API calls failed, notify the user
  if (anyFailed) {
    notify("failure", "Sorry", "One or more files failed to upload. Please try again later, if the issue persists please contact tech support");
    return;
  }

  // Notify the user of the successful upload
  notify("success", "Document Uploaded", "Your document was uploaded successfully, you may need to refresh this page to view the new file.");
  emit("success");

  // Set verification status to 'Under Review'
  if (kyc_status.value != "approved" || kyc_status.value != "pending") kyc_status.value = "pending";
  if (aml_status.value != "approved" || aml_status.value != "pending") aml_status.value = "pending";

  // Reset all files (to include newly uploaded file)
  setInvestorDocuments();
}
// Handle file upload END

// Notification START
const notification = ref(null);
const notificationType = ref("success");
const notificationTitle = ref("File Uploaded");
const notificationText = ref("Your file was uploaded successfully, refresh this page to access the new file");
const notify = (type, title, text) => {
  notificationType.value = type;
  notificationTitle.value = title;
  notificationText.value = text;
  notification.value.show();
};
// Notification END

// Mounted START
const loading = ref(true);
onMounted(async () => {
  // Set documents array
  await setInvestorDocuments();

  if (verification_status.value != 'Verified') {
    showEditor.value = true;  
  }
  
  // Ready
  loading.value = false;
});
</script>

<style scoped>
.custom-bg {
  box-shadow: 0 0 20px 20px white inset;
  background-color: transparent;
  background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f9fafb' fill-opacity='1'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.dark .custom-bg {
  box-shadow: 0 0 20px 20px #262626 inset;
  background-color: transparent;
  background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23222222' fill-opacity='1'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}
</style>
